import React, {
  useCallback, useEffect, useMemo, useState, Fragment, ReactElement,
} from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import {
  useNavigate, useLocation, useParams, Navigate,
} from 'react-router';
import CheckAccess from '../../../components/check-access/check-access.component';
import ContentHeader from '../../../components/content-header/content-header.component';
import StatusBar from '../../../components/status-bar/status-bar.component';
import PageTabs from '../../../components/page-tabs/page-tabs.component';
import ConfirmModal from '../../../components/confirm-modal/confirm-modal.component';
import Loader from '../../../components/loader/loader.component';
import PullToRefresh from '../../../components/pull-to-refresh/pull-to-refresh.component';
import LeaveConfirmModal from '../../../components/leave-confirm-modal/leave-confirm-modal.component';
import Form from '../../../components/form/form.component';
import FormError from '../../../components/form-error/form-error.component';
import FormContainer from '../../../components/form-container/form-container.component';
import LimitedPageContainer from '../../../components/limited-page-block/limited-page-container.component';
import PageContainer from '../../../components/page-container/page-container.component';
import PageContainerSide from '../../../components/page-container-side/page-container-side.component';
import PageContainerMain from '../../../components/page-container-main/page-container-main.component';
import ImageContainer from '../../../components/image-container/image-container.component';
import Controls from '../../app/controls/controls.component';
import PortfolioModal from '../../asset-list/portfolio-modal/portfolio-modal.component';
import SettingsPageTabs from '../settings-page-tabs/settings-page-tabs.component';
import SettingsPortfolioList from './settings-portfolio-list/settings-portfolio-list.container';
import SettingsPortfolioGroupGeneral
  from './settings-portfolio-group-general/settings-portfolio-group-general.component';
import SettingsPortfolioGroupValidator from './settings-portfolio-group.validator';
import FormatService from '../../../services/format-service';
import ScreenService from '../../../services/screen-service';
import FormService from '../../../services/form-service';
import StructureService from '../../../services/structure-service';
import useServerFormErrors from '../../../hooks/use-server-form-errors';
import useEditModeQuery from '../../../hooks/use-edit-mode-query';
import useFormBlock from '../../../hooks/use-form-block';
import useFormStructureModified from '../../../hooks/use-form-structure-modified';
import {
  getPortfolioGroupData, savePortfolioGroupData, getPortfolioDealEmailConnectorData, resetDealConnectorAuthLink,
  verifyPortfolioDealEmailConnector, deletePortfolioGroupPortfolio,
} from './settings-portfolio-group.action';
import { toggleEditMode } from '../../app/controls/controls.action';
import { resetFormErrors } from '../../app/app.action';
import { TAB_INDEX_DEFAULT } from '../../../constants/system/tab-indexes';
import { PORTFOLIO_CREATE, PORTFOLIO_GROUP_EDIT } from '../../../constants/entity/permissions';
import { FORM_ID_SETTINGS_PORTFOLIO_GROUP } from '../../../constants/system/form-ids';
import { ROUTE_NOT_FOUND, ROUTE_PARAM_ID, ROUTE_PORTFOLIO_GROUP } from '../../../constants/system/routes';
import {
  QUERY_PARAM_TAB, QUERY_PARAM_EMAIL_CONNECTOR_CODE, QUERY_PARAM_PORTFOLIO_ID,
} from '../../../constants/system/query-params';
import {
  TAB_NAME_PORTFOLIO_MANAGEMENT, TAB_NAME_PORTFOLIO_LIST, TAB_NAME_GENERAL,
} from '../../../constants/system/tab-names';
import { MARKER_COLOR_RED } from '../../../constants/entity/marker-colors';
import AssetDefaultImage from '../../../static/images/asset-default-image.png';

const SettingsPortfolioGroup: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const {
    isEditMode,
    isDataLoaded,
    isRightDrawerOpen,
    currentUser,
    portfolioGroupData,
    portfolioMarkerColorList,
    portfolioGroupList,
    dealConnectorAuthLink,
    form: formProps,
    modified: formModifiedFields,
    handleSubmit: submitForm,
    pristine: isFormPristine,
    isSubmitting: isFormSaving,
    error: formError,
    initialValues: initialFormValues,
    values: formValues,
    hasValidationErrors: withFormErrors,
    formErrors: serverFormErrors,
    mainFormError: mainServerFormError,
  } = props;

  const {
    reset: resetForm,
    change: changeForm,
  } = formProps;

  const routeParams: ReactRouterParams = useParams();
  const navigate: ReactRouterNavigate = useNavigate();
  const location: ReactRouterLocation = useLocation();
  const dispatch: ReduxDispatch = useDispatch();
  const isFormStructureModified: boolean = useFormStructureModified(formModifiedFields, isDataLoaded);

  const portfolioGroupId: number = FormatService.parseInt(routeParams[ROUTE_PARAM_ID]);
  const locationQueryParams: ParsedQueryParams | null = FormatService.parseQueryParams(location.search);
  const queryTab: string | null = locationQueryParams && locationQueryParams[QUERY_PARAM_TAB]
    ? FormatService.parseStringParam(locationQueryParams[QUERY_PARAM_TAB].toString()) : null;
  const queryEmailConnectorCode: string | null = locationQueryParams
  && locationQueryParams[QUERY_PARAM_EMAIL_CONNECTOR_CODE]
    ? FormatService.parseStringParam(locationQueryParams[QUERY_PARAM_EMAIL_CONNECTOR_CODE].toString()) : null;
  const queryPortfolioId: number | null = locationQueryParams && locationQueryParams[QUERY_PARAM_PORTFOLIO_ID]
    ? FormatService.parseInt(locationQueryParams[QUERY_PARAM_PORTFOLIO_ID].toString()) : null;
  const isPortfolioListTabActive: boolean = !!queryTab && queryTab === TAB_NAME_PORTFOLIO_LIST;

  const [isOpenMessage, setIsOpenMessage]: [boolean, Function] = useState(false);
  const [statusMessage, setStatusMessage]: [string, Function] = useState('');
  const [isLeaveConfirmed, setIsLeaveConfirmed]: [boolean, Function] = useState(false);
  const [isLeaveConfirmModalOpen, setIsLeaveConfirmModalOpen]: [boolean, Function] = useState(false);
  const [lastLocation, setLastLocation]: [ReactRouterLocation | null, Function] = useState<StateValueDefault>(null);
  const [lastTabIndex, setLastTabIndex]: [number | null, Function] = useState(null);
  const [isPortfolioGroupPhotoDeleted, setIsPortfolioGroupPhotoDeleted]: [boolean, Function] = useState(false);
  const [portfolioGroupPhotoFile, setPortfolioGroupPhotoFile]: [File | null, Function] = useState(null);
  const [isPortfolioModalOpen, setIsPortfolioModalOpen]: [boolean, Function] = useState(false);
  const [isNewPortfolio, setIsNewPortfolio]: [boolean, Function] = useState(false);
  const [portfolioActiveId, setPortfolioActiveId]: [number | null, Function] = useState(null);
  const [portfolioActiveIds, setPortfolioActiveIds]: [number[] | null, Function] = useState<StateValueDefault>(null);
  const [isDeleteConfirmModalOpen, setIsDeleteConfirmModalOpen]: [boolean, Function] = useState(false);
  const [confirmModalErrorMessage, setConfirmModalErrorMessage]: [string | null, Function] = useState(null);
  const [inlineSavePortfolioId, setInlineSavePortfolioId]: [number | null, Function] = useState(null);
  const [inlineSaveIsConfirm, setInlineSaveIsConfirm]: [boolean | null, Function] = useState(null);

  const pageTitle: string = `Portfolio Group${portfolioGroupData ? `: ${portfolioGroupData.name}` : ''}`;
  const createButtonTitle: string | null = isPortfolioListTabActive && !isEditMode ? 'New Portfolio' : null;
  const deleteConfirmModalTitle: string = 'Delete Portfolio';
  const deleteConfirmModalText: string = 'Are you sure you want to delete this portfolio?';
  const isFormDataPristine: boolean = isFormPristine && !portfolioGroupPhotoFile && !isPortfolioGroupPhotoDeleted;
  const isFormChanged: boolean = isEditMode && !isFormDataPristine;
  const isFormStateChanged: boolean = isEditMode;
  const isSaveDisabled: boolean = isFormSaving || (isFormDataPristine && !isFormStructureModified);
  const isEditModeQueryDisabled: boolean = isLeaveConfirmed && (!!lastLocation || (lastTabIndex !== null
    && lastTabIndex >= TAB_INDEX_DEFAULT));
  const portfolioList: Portfolio[] = portfolioGroupData ? portfolioGroupData.portfolios : null;
  const formKeyNew: string = FormService.getIndexedFormFieldKey(FormService.FORM_KEY_NEW);
  const withServerErrors: boolean = !!serverFormErrors && !!Object.keys(serverFormErrors).length;

  const portfolioGroupEditPermissions: string[] = useMemo((): string[] => [PORTFOLIO_GROUP_EDIT], []);
  const saveFormDeps: DepsItem[] = useMemo((): DepsItem[] => [formValues], [formValues]);

  const checkIsPortfolioActive: Function = useCallback((portfolioId: number): boolean => (
    portfolioActiveIds ? portfolioActiveIds.includes(portfolioId) : false
  ), [portfolioActiveIds]);

  const onCancelForm: Function = useCallback((): void => {
    if (inlineSavePortfolioId !== null) {
      if (isPortfolioModalOpen) {
        closePortfolioModal();
      }

      inlineCancel();
    } else {
      if (isPortfolioModalOpen) {
        closePortfolioModal();
      }

      FormService.forceResetForm(resetForm, formProps);

      setIsPortfolioGroupPhotoDeleted(false);
      setPortfolioGroupPhotoFile(null);
      setPortfolioActiveIds([]);

      if (!isNewPortfolio || !isPortfolioListTabActive) {
        dispatch(toggleEditMode(false));
      }
    }
  }, [
    isNewPortfolio, inlineSavePortfolioId, portfolioActiveIds, isPortfolioModalOpen, isPortfolioListTabActive,
    isEditMode,
  ]);

  const onLeaveConfirm: Function = useCallback((): void => {
    setIsLeaveConfirmed(true);
    setIsLeaveConfirmModalOpen(false);

    onCancelForm();
  }, [
    isNewPortfolio, inlineSavePortfolioId, portfolioActiveIds, isPortfolioModalOpen, isPortfolioListTabActive,
    isEditMode,
  ]);

  const closeLeaveConfirmModal: Function = useCallback((): void => {
    setIsLeaveConfirmModalOpen(false);
  }, []);

  const onPortfolioGroupPhotoChange: Function = useCallback((file: File): void => {
    setIsPortfolioGroupPhotoDeleted(false);
    setPortfolioGroupPhotoFile(file);
    changeForm('photo', file);
  }, []);

  const onPortfolioGroupPhotoDelete: MouseEventHandlerFunction = useCallback((
    event: React.MouseEvent<Element, MouseEvent>,
  ): void => {
    setIsPortfolioGroupPhotoDeleted(true);
    setPortfolioGroupPhotoFile(null);
    changeForm('photo', null);

    event.stopPropagation();
  }, []);

  const openPortfolioDeleteConfirmModal: Function = useCallback((portfolioId: number): void => {
    setPortfolioActiveId(portfolioId);
    setIsDeleteConfirmModalOpen(true);
  }, []);

  const closePortfolioDeleteConfirmModal: Function = useCallback((): void => {
    setIsDeleteConfirmModalOpen(false);
    setConfirmModalErrorMessage(null);
    setPortfolioActiveId(null);
  }, []);

  const onPortfolioDeleteConfirm: Function = useCallback((): Promise<ActionCallback> | null => {
    if (!portfolioActiveId) {
      return null;
    }

    return new Promise((resolve: ActionResolveCallback): void => {
      dispatch(deletePortfolioGroupPortfolio({
        portfolioId: portfolioActiveId,
        portfolioGroupId,
      }, (response: ActionResponse): void => {
        setIsDeleteConfirmModalOpen(false);
        setConfirmModalErrorMessage(null);

        setPortfolioActiveId(null);

        setStatusMessage('Deleted successfully');
        setIsOpenMessage(true);

        resolve(response);
      }, (exception: ServerError): void => {
        if (exception && exception.message) {
          setConfirmModalErrorMessage(exception.message);
        }
      }));
    });
  }, [portfolioActiveId, portfolioGroupId]);

  const closeMessage: Function = useCallback((): void => {
    setIsOpenMessage(false);
    setStatusMessage('');
  }, []);

  const onFormLeave: FormLeaveFunction = useCallback((nextLocation: ReactRouterLocation): boolean => {
    if (isFormChanged) {
      setIsLeaveConfirmModalOpen(true);

      setLastLocation(nextLocation);

      return false;
    }

    if ((!isNewPortfolio || !isPortfolioListTabActive) && isEditMode) {
      dispatch(toggleEditMode(false));
    }

    return true;
  }, [isFormChanged, isNewPortfolio, isPortfolioListTabActive, isEditMode]);

  const onBeforeLocationChange: Function = useCallback((nextLocation: ReactRouterLocation): void => {
    if (isFormChanged) {
      setIsLeaveConfirmModalOpen(true);

      setLastLocation(nextLocation);
    }
  }, [isFormChanged]);

  const onBeforeTabChange: Function = useCallback((newTabIndex: number): void => {
    if (isFormChanged) {
      setIsLeaveConfirmModalOpen(true);

      setLastTabIndex(newTabIndex);
    }
  }, [isFormChanged]);

  const onCreatePortfolioButtonClick: Function = useCallback((): void => {
    setIsPortfolioModalOpen(true);
    setIsNewPortfolio(true);

    if (isEditMode) {
      setInlineSaveIsConfirm(null);
      setInlineSavePortfolioId(FormService.FORM_KEY_NEW);
    }
  }, [isEditMode]);

  const closePortfolioModal: Function = useCallback((): void => {
    setIsNewPortfolio(false);
    setIsPortfolioModalOpen(false);

    if (isEditMode) {
      setInlineSaveIsConfirm(null);
      setInlineSavePortfolioId(FormService.FORM_KEY_NEW);
    }
  }, [isEditMode]);

  const inlineCancel: Function = useCallback((): void => {
    const portfolioId: number | null = inlineSavePortfolioId;

    if (portfolioId) {
      const formKeyPortfolio: string = FormService.getIndexedFormFieldKey(portfolioId);

      if (initialFormValues && initialFormValues.portfolios) {
        changeForm(`portfolios[${formKeyPortfolio}]`, initialFormValues.portfolios[formKeyPortfolio]);
      }

      if (portfolioActiveIds && portfolioId !== FormService.FORM_KEY_NEW) {
        const portfolioActiveIdIndex: number = portfolioActiveIds.findIndex((
          portfolioItemActiveId: number,
        ): boolean => (
          portfolioItemActiveId === portfolioId
        ));

        if (portfolioActiveIdIndex !== FormatService.INDEX_NOT_FOUND) {
          const newPortfolioActiveIds: number[] = [...portfolioActiveIds];

          StructureService.deleteListItemByIndex(newPortfolioActiveIds, portfolioActiveIdIndex);
          setPortfolioActiveIds(newPortfolioActiveIds);
        }
      }
    }

    setInlineSaveIsConfirm(null);
    setInlineSavePortfolioId(null);
  }, [initialFormValues, portfolioActiveIds, inlineSavePortfolioId]);

  const sendForm: Function = useCallback((values: FormValues): Promise<ActionCallback> | null => {
    if (!values) {
      return null;
    }

    const portfolioGroupFormItem: FormValueItem = {
      ...values,
    };

    if (portfolioGroupPhotoFile) {
      portfolioGroupFormItem.photo = portfolioGroupPhotoFile;
    } else if (isPortfolioGroupPhotoDeleted) {
      portfolioGroupFormItem.photo = null;
    } else {
      delete portfolioGroupFormItem.photo;
    }

    let portfolios: FormValueItem[];
    if (isNewPortfolio) {
      portfolios = [portfolioGroupFormItem.portfolios[formKeyNew]];
    } else {
      delete portfolioGroupFormItem.portfolios[formKeyNew];

      portfolios = Object.values({ ...portfolioGroupFormItem.portfolios });
    }

    portfolioGroupFormItem.portfolios = [];
    if (portfolios.length) {
      portfolios.forEach((portfolio: FormValueItem): void => {
        const portfolioFormItem: FormValueItem = {
          ...portfolio,
        };

        if (portfolioFormItem && (isNewPortfolio
          || (inlineSavePortfolioId && inlineSavePortfolioId === portfolioFormItem.id)
          || (!inlineSavePortfolioId && checkIsPortfolioActive(portfolioFormItem.id)))) {
          portfolioFormItem.capitalAllocated = portfolioFormItem.capitalAllocated
            ? FormatService.formatCleanNumber(portfolioFormItem.capitalAllocated) : 0;

          const portfolioItem: PortfolioInput = portfolioFormItem;

          portfolioGroupFormItem.portfolios.push({ ...portfolioItem });
        }
      });
    }

    const portfolioGroupItem: PortfolioGroupInput = portfolioGroupFormItem;

    return new Promise((resolve: ActionResolveCallback): void => {
      dispatch(savePortfolioGroupData({
        portfolioGroup: portfolioGroupItem,
      }, (response: ActionResponse): void => {
        onCancelForm();

        setStatusMessage('Saved successfully');
        setIsOpenMessage(true);

        resolve(response);
      }, (exception: ServerError): void => {
        setInlineSavePortfolioId(null);
        setInlineSaveIsConfirm(null);

        if (exception && exception.message) {
          submitForm();
        }
      }));
    });
  }, [
    isSaveDisabled, portfolioGroupPhotoFile, isPortfolioGroupPhotoDeleted, isNewPortfolio, isPortfolioModalOpen,
    inlineSavePortfolioId, portfolioActiveIds, queryTab, isPortfolioListTabActive, isEditMode, formKeyNew,
  ]);

  const onSubmitForm: Function = useCallback((): void => {
    submitForm();

    if (!withFormErrors) {
      sendForm(formValues);
    } else {
      FormService.focusOnFirstFormError();
    }
  }, [
    isSaveDisabled, portfolioGroupPhotoFile, isPortfolioGroupPhotoDeleted, isNewPortfolio, isPortfolioModalOpen,
    inlineSavePortfolioId, portfolioActiveIds, queryTab, isPortfolioListTabActive, isEditMode, formKeyNew,
    withFormErrors, formValues,
  ]);

  const onInlineSave: Function = useCallback((portfolioId: number): void => {
    setInlineSavePortfolioId(portfolioId);
    setInlineSaveIsConfirm(true);
  }, []);

  const onInlineCancel: Function = useCallback((portfolioId: number): void => {
    setInlineSavePortfolioId(portfolioId);
    setInlineSaveIsConfirm(false);
  }, []);

  const onPortfolioModalCancel: Function = useCallback((): void => {
    if (isEditMode) {
      setInlineSavePortfolioId(FormService.FORM_KEY_NEW);
      setInlineSaveIsConfirm(false);
    } else {
      onCancelForm();
    }
  }, [
    isEditMode, portfolioActiveIds, isNewPortfolio, inlineSavePortfolioId, isPortfolioModalOpen,
    isPortfolioListTabActive,
  ]);

  const onPortfolioCreate: Function = useCallback((): void => {
    if (isEditMode) {
      setInlineSavePortfolioId(FormService.FORM_KEY_NEW);
      setInlineSaveIsConfirm(true);
    } else {
      onSubmitForm();
    }
  }, [
    isEditMode, isSaveDisabled, portfolioGroupPhotoFile, isPortfolioGroupPhotoDeleted, isNewPortfolio,
    isPortfolioModalOpen, initialFormValues, inlineSavePortfolioId, portfolioActiveIds, formKeyNew,
    withFormErrors, formValues,
  ]);

  const onPortfolioSubmitCreateForm: Function = useCallback((onSubmitCallback?: Function): void => {
    submitForm();

    if (!withFormErrors && onSubmitCallback) {
      onSubmitCallback();
    }
  }, [withFormErrors]);

  const onDealConnectEmailClick: Function = useCallback((portfolioId: number): void => {
    if (portfolioId) {
      dispatch(getPortfolioDealEmailConnectorData({
        portfolioId,
      }));
    }
  }, []);

  const onInlineEditModeButtonClick: Function = useCallback((
    event: React.MouseEvent<Element, MouseEvent>, portfolioId: number,
  ): void => {
    setPortfolioActiveIds([...(portfolioActiveIds || []), portfolioId]);

    if (!isEditMode) {
      dispatch(toggleEditMode(true));
    }

    event.stopPropagation();
  }, [isEditMode, portfolioActiveIds]);

  const onRefresh: Function = useCallback((): void => {
    if (portfolioGroupId) {
      dispatch(getPortfolioGroupData({
        portfolioGroupId,
      }));
    }
  }, [portfolioGroupId]);

  const tabs: TabItem[] = [
    {
      id: 1,
      name: TAB_NAME_GENERAL,
      title: 'General',
      component: (
        <SettingsPortfolioGroupGeneral
          isSaving={isFormSaving}
          isSaveDisabled={isSaveDisabled}
          isEditMode={isEditMode}
          isRightDrawerOpen={isRightDrawerOpen}
          formError={formError}
          portfolioGroupData={portfolioGroupData}
          currentUser={currentUser}
          onCancelForm={onCancelForm}
          onSubmitForm={onSubmitForm}
        />
      ),
    },
    {
      id: 2,
      name: TAB_NAME_PORTFOLIO_LIST,
      title: 'Portfolio List',
      component: (
        <SettingsPortfolioList
          isSaving={isFormSaving}
          isSaveDisabled={isSaveDisabled}
          isEditMode={isEditMode}
          isRightDrawerOpen={isRightDrawerOpen}
          portfolioActiveIds={portfolioActiveIds}
          portfolioList={portfolioList}
          portfolioMarkerColorList={portfolioMarkerColorList}
          portfolioGroupList={portfolioGroupList}
          portfolioGroupData={portfolioGroupData}
          formError={formError}
          inlineSavePortfolioId={inlineSavePortfolioId}
          onInlineSave={onInlineSave}
          onInlineCancel={onInlineCancel}
          onCancelForm={onCancelForm}
          onSubmitForm={onSubmitForm}
          onDealConnectEmailClick={onDealConnectEmailClick}
          onPortfolioDeleteClick={openPortfolioDeleteConfirmModal}
          onInlineEditModeButtonClick={onInlineEditModeButtonClick}
        />
      ),
    },
  ];

  const onTabChange: Function = useCallback((newTabId: number): void => {
    const queryParams: LocationQueryParams = locationQueryParams || {};

    onCancelForm();

    tabs.forEach((tabItem: TabItem): void => {
      if (tabItem.id === newTabId) {
        queryParams[QUERY_PARAM_TAB] = tabItem.name;

        navigate({
          pathname: `${ROUTE_PORTFOLIO_GROUP}/${portfolioGroupData.id}`,
          search: FormatService.formatQueryParams(queryParams),
        });
      }
    });
  }, [
    locationQueryParams, tabs, portfolioGroupData, isNewPortfolio, inlineSavePortfolioId, portfolioActiveIds,
    isPortfolioModalOpen, isPortfolioListTabActive, isEditMode,
  ]);

  const onFormChange: Function = useCallback((event: React.SyntheticEvent): void => {
    const fieldName: string = event.target && (event.target as HTMLInputElement).name;

    if (withServerErrors && fieldName) {
      dispatch(resetFormErrors());
      FormService.resetFormServerErrors(formProps, fieldName);
      submitForm();
    }
  }, [withServerErrors, formProps]);

  useEffect((): void => {
    if (portfolioGroupId) {
      dispatch(getPortfolioGroupData({
        portfolioGroupId,
      }));
    }
  }, [portfolioGroupId]);

  useEffect((): void => {
    if (isLeaveConfirmed) {
      if (lastLocation) {
        navigate({
          pathname: lastLocation.pathname,
          search: lastLocation.search || '',
        });

        setLastLocation(undefined);
      }

      if (lastTabIndex !== null && lastTabIndex >= TAB_INDEX_DEFAULT) {
        const queryParams: LocationQueryParams = {};
        const currentTab: TabItem = tabs[lastTabIndex];

        if (currentTab) {
          queryParams[QUERY_PARAM_TAB] = currentTab.name;

          navigate({
            pathname: `${ROUTE_PORTFOLIO_GROUP}/${portfolioGroupId}`,
            search: FormatService.formatQueryParams(queryParams),
          });
        }

        setLastTabIndex(null);
      }

      setIsLeaveConfirmed(false);
    }
  }, [isLeaveConfirmed]);

  useEffect((): void => {
    if (lastLocation) {
      setLastTabIndex(null);
    }
  }, [lastLocation]);

  useEffect((): void => {
    if (dealConnectorAuthLink) {
      dispatch(resetDealConnectorAuthLink());
      ScreenService.openLink(dealConnectorAuthLink);
    }
  }, [dealConnectorAuthLink]);

  useEffect((): void => {
    if (queryEmailConnectorCode && queryPortfolioId) {
      const queryParams: LocationQueryParams = {};

      dispatch(verifyPortfolioDealEmailConnector({
        verificationForm: {
          code: queryEmailConnectorCode,
          portfolioId: queryPortfolioId,
        },
      }, (): void => {
        queryParams[QUERY_PARAM_TAB] = TAB_NAME_PORTFOLIO_LIST;

        navigate({
          pathname: `${ROUTE_PORTFOLIO_GROUP}/${portfolioGroupId}`,
          search: FormatService.formatQueryParams(queryParams),
        });
      }));
    }
  }, [queryEmailConnectorCode, queryPortfolioId]);

  useEffect((): void => {
    if (inlineSaveIsConfirm !== null && inlineSavePortfolioId !== null) {
      if (inlineSaveIsConfirm) {
        onSubmitForm();
      } else {
        onCancelForm();
      }
    }
  }, [inlineSaveIsConfirm]);

  useEffect((): void => {
    if (withServerErrors && inlineSaveIsConfirm !== null) {
      setInlineSavePortfolioId(null);
      setInlineSaveIsConfirm(null);
    }
  }, [withServerErrors]);

  useServerFormErrors(serverFormErrors, (): void => {
    submitForm();
  });

  useEditModeQuery(isEditModeQueryDisabled);

  useFormBlock(onFormLeave, isFormChanged, isFormStateChanged, isLeaveConfirmed, isLeaveConfirmModalOpen);

  if (!portfolioGroupId) {
    return <Navigate to={ROUTE_NOT_FOUND} />;
  }

  return (
    <CheckAccess permissions={portfolioGroupEditPermissions}>
      <Form id={FORM_ID_SETTINGS_PORTFOLIO_GROUP} onChange={onFormChange}>
        <PullToRefresh isDisabled={isEditMode} onRefresh={onRefresh}>
          <ContentHeader title={pageTitle} />
          <SettingsPageTabs
            isDisabled={isEditMode}
            isCompressed={isRightDrawerOpen}
            activeTab={TAB_NAME_PORTFOLIO_MANAGEMENT}
            currentUser={currentUser}
            onBeforeLocationChange={onBeforeLocationChange}
          />
          {
            !isDataLoaded && (
              <Loader isPageLoader withTopOffset />
            )
          }
          {
            isDataLoaded && (
              <Fragment>
                <Controls
                  isSaveDisabled={isSaveDisabled}
                  editPermissions={portfolioGroupEditPermissions}
                  createButtonTitle={createButtonTitle}
                  createPermission={PORTFOLIO_CREATE}
                  onCancelForm={onCancelForm}
                  onSubmitForm={onSubmitForm}
                  onCreateButtonClick={onCreatePortfolioButtonClick}
                />
                <PageContainer>
                  <PageContainerSide>
                    <ImageContainer
                      isEditMode={isEditMode}
                      isImageDeleted={isPortfolioGroupPhotoDeleted}
                      name={portfolioGroupData.name}
                      image={portfolioGroupData.photo}
                      imageThumbnail={portfolioGroupData.photoThumbnail}
                      imageFile={portfolioGroupPhotoFile}
                      imageFormFieldName="photo"
                      defaultImage={AssetDefaultImage}
                      onImageFileChange={onPortfolioGroupPhotoChange}
                      onImageFileDelete={onPortfolioGroupPhotoDelete}
                    />
                  </PageContainerSide>
                  <PageContainerMain>
                    <LimitedPageContainer isActive>
                      <PageTabs
                        isSubTabs
                        isDisabled={isFormChanged}
                        isCompressed={isRightDrawerOpen}
                        tabs={tabs}
                        currentTab={queryTab}
                        onTabChange={onTabChange}
                        onBeforeTabChange={onBeforeTabChange}
                      />
                    </LimitedPageContainer>
                  </PageContainerMain>
                </PageContainer>
              </Fragment>
            )
          }
          {
            (!!formError || !!mainServerFormError) && !isFormSaving && (
              <FormError
                withShowError={isEditMode}
                withLeftOffset={isRightDrawerOpen}
                error={formError || mainServerFormError}
              />
            )
          }
        </PullToRefresh>
        <PortfolioModal
          isPortfolioSettings
          isPortfolioGroupDisabled
          isOpen={isPortfolioModalOpen}
          isSaving={isFormSaving}
          isSaveDisabled={isSaveDisabled}
          isEditMode={isEditMode}
          portfolioId={FormService.FORM_KEY_NEW}
          portfolioGroupList={portfolioGroupList}
          portfolioMarkerColorList={portfolioMarkerColorList}
          formPrefixCommon="portfolios"
          saveFormDeps={saveFormDeps}
          onCreate={onPortfolioCreate}
          onCancel={onPortfolioModalCancel}
          onCloseModal={closePortfolioModal}
          onSubmitCreateForm={onPortfolioSubmitCreateForm}
        />
        <LeaveConfirmModal
          isOpen={isLeaveConfirmModalOpen}
          onConfirm={onLeaveConfirm}
          onCloseModal={closeLeaveConfirmModal}
        />
        <ConfirmModal
          isOpen={isDeleteConfirmModalOpen}
          title={deleteConfirmModalTitle}
          text={deleteConfirmModalText}
          errorMessage={confirmModalErrorMessage}
          onConfirm={onPortfolioDeleteConfirm}
          onCloseModal={closePortfolioDeleteConfirmModal}
        />
        <StatusBar isOpen={isOpenMessage} message={statusMessage} onClose={closeMessage} />
      </Form>
    </CheckAccess>
  );
});

const initialValues: FormValueItem = {};
const mapState: ReduxMapStateCallback = (reducers: Reducers): ReduxMapState => {
  const {
    app,
    controls,
    rightDrawer,
    settingsPortfolioGroup,
  } = reducers;

  const formKeyNew: string = FormService.getIndexedFormFieldKey(FormService.FORM_KEY_NEW);

  const portfolioGroupData: PortfolioGroup = settingsPortfolioGroup.portfolioGroupData
    && settingsPortfolioGroup.portfolioGroupData.portfolioGroup;

  if (portfolioGroupData) {
    initialValues.id = portfolioGroupData.id;
    initialValues.name = portfolioGroupData.name;
    initialValues.photo = null;
    initialValues.portfolios = {
      [formKeyNew]: {
        id: FormService.FORM_KEY_NEW,
        name: null,
        groupId: portfolioGroupData && portfolioGroupData.id,
        markerColor: MARKER_COLOR_RED,
        isHidden: false,
      },
    };

    if (portfolioGroupData.portfolios.length) {
      portfolioGroupData.portfolios.forEach((portfolioItem: Portfolio): void => {
        const formKeyPortfolio: string = FormService.getIndexedFormFieldKey(portfolioItem.id);

        initialValues.portfolios[formKeyPortfolio] = {
          id: portfolioItem.id,
          name: portfolioItem.name,
          capitalAllocated: FormatService.formatNumber(portfolioItem.capitalAllocated),
          groupId: portfolioGroupData.id,
          markerColor: portfolioItem.markerColor ? portfolioItem.markerColor.name : MARKER_COLOR_RED,
          isHidden: portfolioItem.isHidden || false,
          dealConnectorEmail: portfolioItem.dealConnectorEmail,
        };
      });
    }
  }

  return {
    isDataLoaded: !!settingsPortfolioGroup.portfolioGroupData,
    isEditMode: controls.isEditMode,
    isRightDrawerOpen: rightDrawer.isDrawerOpen,
    currentUser: app.baseData && app.baseData.currentUser,
    portfolioMarkerColorList: settingsPortfolioGroup.portfolioGroupData
      && settingsPortfolioGroup.portfolioGroupData.portfolioMarkerColorList,
    portfolioGroupList: settingsPortfolioGroup.portfolioGroupData
      && settingsPortfolioGroup.portfolioGroupData.portfolioGroupList,
    dealConnectorAuthLink: settingsPortfolioGroup.dealConnectorAuthLink,
    portfolioGroupData,
  };
};

const SettingsPortfolioGroupForm: ReactComponent = (props: ReactComponentProps): ReactElement => {
  const formErrors: ValidationErrors = useSelector(({ settingsPortfolioGroup }: Reducers): ValidationErrorsMap => (
    settingsPortfolioGroup.formErrors
  ));
  const isFormSaving: ValidationErrors = useSelector(({ settingsPortfolioGroup }: Reducers): ValidationErrorsMap => (
    settingsPortfolioGroup.isFormSaving
  ));

  return (
    <FormContainer
      formId={FORM_ID_SETTINGS_PORTFOLIO_GROUP}
      component={SettingsPortfolioGroup}
      validator={SettingsPortfolioGroupValidator}
      initialValues={initialValues}
      formErrors={formErrors}
      isSubmitting={isFormSaving}
      {...props}
    />
  );
};

export default connect(mapState)(SettingsPortfolioGroupForm);
